import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLine } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import { CATEGORIES } from "../components/EnquiryForm";
import { CategoryImage, CATEGORY_IMAGES } from "../components/CategoryImage";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";
const SURFACES = ["bg-white", "bg-ice", "bg-white", "bg-ice", "bg-white", "bg-mist"];

const CategorySection = ({ c, i, t }) => {
  const imageFirst = i % 2 === 1;
  return (
    <section id={c.id} data-testid={`product-section-${c.id}`} className={`${SURFACES[i]} scroll-mt-nav`}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center py-16 md:py-24`}>
        <div className={`lg:col-span-7 ${imageFirst ? "lg:order-last" : ""}`}>
          <Reveal>
            <p className="font-mono text-sm font-medium text-pine">0{i + 1}</p>
            <h2 className="font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink mt-5">{c.name}</h2>
            <p className="text-slate leading-relaxed mt-5 max-w-xl text-base md:text-lg">{c.desc}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-mono text-xs font-medium tracking-[0.18em] uppercase text-slate mt-10 mb-5">{t("productsPage.scopeLabel")}</p>
            <ul className="space-y-3 max-w-xl border-t border-ink/10">
              {c.scope.map((d) => (
                <li key={d} className="flex gap-4 text-[15px] text-ink/85 leading-relaxed pt-3">
                  <span className="w-3 h-[2px] bg-pine mt-[11px] shrink-0" aria-hidden="true" />
                  {d}
                </li>
              ))}
            </ul>
            <LocalizedLink
              to={`/contact?category=${encodeURIComponent(CATEGORIES[i])}`}
              data-testid={`enquire-${c.id}`}
              className="group inline-flex items-center gap-2 mt-10 border border-ink text-ink bg-white font-semibold px-6 py-3 rounded text-sm transition-colors duration-300 hover:bg-ink hover:text-white"
            >
              {t("productsPage.enquire")}
              <ArrowUpRight size={15} className="rtl:-scale-x-100" />
            </LocalizedLink>
          </Reveal>
        </div>
        <div className={`lg:col-span-5 ${imageFirst ? "lg:-ms-10 xl:-ms-20" : "lg:-me-10 xl:-me-20"}`}>
          <div className="fade-edges overflow-hidden -mx-2 sm:mx-0">
            <CategoryImage
              id={c.id}
              alt={c.alt}
              eager={i === 0}
              sizes="(min-width: 1240px) 600px, (min-width: 1024px) 48vw, 100vw"
              position={CATEGORY_IMAGES[c.id]?.mobile}
              className="w-full h-auto aspect-[4/3] sm:aspect-[3/2] object-cover lg:aspect-auto lg:object-contain"
              testid={`product-image-${c.id}`}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Products = () => {
  usePageSeo("products");
  const { t } = useTranslation();
  const categories = t("categories.items", { returnObjects: true });

  return (
    <main className="pt-nav">
      <section data-testid="products-hero" className="bg-white border-b border-ice">
        <div className={`${WRAP} py-16 md:py-24`}>
          <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine mb-8">{t("productsPage.eyebrow")}</p>
          <h1 className="font-display font-semibold text-ink text-[36px] md:text-[44px] tracking-tight leading-[1.12] max-w-4xl" data-testid="products-headline">
            <MaskedLine delay={0.1}>{t("productsPage.line1")}</MaskedLine>
            <MaskedLine delay={0.2}>{t("productsPage.line2")}</MaskedLine>
          </h1>
          <p className="text-slate leading-relaxed mt-8 max-w-2xl text-base md:text-lg">{t("productsPage.intro")}</p>
        </div>
      </section>

      {(Array.isArray(categories) ? categories : []).map((c, i) => (
        <CategorySection key={c.id} c={c} i={i} t={t} />
      ))}

      <section className="bg-white border-t border-ice">
        <div className={`${WRAP} py-16 md:py-20`}>
          <Reveal>
            <div className="max-w-3xl" data-testid="products-availability-note">
              <p className="font-mono text-xs font-medium tracking-[0.18em] uppercase text-pine mb-4">{t("productsPage.noteLabel")}</p>
              <p className="text-slate leading-relaxed">{t("productsPage.noteText")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Products;
